import {
  AfterViewInit, ChangeDetectorRef,
  Component, DoCheck,
  ElementRef, EventEmitter,
  Host,
  Input,
  OnDestroy,
  Optional, Output,
  SkipSelf,
  ViewChild
} from '@angular/core';
import {AbstractControl, ControlContainer, ControlValueAccessor, Form, NG_VALUE_ACCESSOR} from '@angular/forms';
import {Subscription} from 'rxjs/Subscription';
import {coerceBooleanProperty, coerceNumberProperty} from '@angular/cdk/coercion';
import {FormSelectItem} from '@common/form-components';
import { Pipe, PipeTransform } from '@angular/core';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {Subject} from 'rxjs/Subject';
import {fromEvent} from 'rxjs/observable/fromEvent';
import {ConnectionPositionPair} from '@angular/cdk/overlay';

@Component({
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: FormSelectMultipleComponent,
    }
  ],
  selector: 'app-form-select-multiple',
  templateUrl: './form-select-multiple.component.html',
  styleUrls: ['./form-select-multiple.component.scss', '../form-components-styles.scss']
})
export class FormSelectMultipleComponent implements AfterViewInit, OnDestroy, ControlValueAccessor, DoCheck {

  // Label del Input
  @Input() label = '';

  // Mantener el espacio del label, desactivar para casos donde no se requiere label
  _keepLabelSpace = true;
  @Input() set keepLabelSpace(val: boolean) {
    this._keepLabelSpace = coerceBooleanProperty(val);
  }

  // Placeholder del Select
  @Input() placeholder = '';

  // Mensaje de error Inferior (solo acepta uno, podria prepararse una property para un array y mostrar en vertical)
  @Input() errorMessage = '';

  // Mantener el espacio del error, desactivar si no hay validaciones (mantener en formularios para evitar conflictos de tamaño de inputs)
  _keepErrorSpace = true;
  @Input() set keepErrorSpace(val: boolean) {
    this._keepErrorSpace = coerceBooleanProperty(val);
  }

  _trimValueAmount = 0;
  @Input() set trimValueAmount(val: number) {
    this._trimValueAmount = coerceNumberProperty(val);
  }

  // Icono de google (fonts.google.com/icons)
  @Input() googleIconName = '';

  // Icono SVG local
  @Input() svgIconName = '';

  _menuOptions: FormSelectMultipleItem[] = [];
  @Input() set menuOptions(val: FormSelectMultipleItem[]) {
    val.forEach(item => {
      if (this.value && this.value.length) {
        item.selected = this.value.find(x => x.Id === item.Id) !== undefined;
      } else {
        item.selected = false;
      }
    });
    this._menuOptions = [...val];
    this._allOptions = [...val];
    this.sortSelectedFirst();
    window.setTimeout(() => {
      if (this.overlayIsOpen) {
        this.overlayIsOpen = false;
        window.setTimeout(() => {
          this.overlayIsOpen = true;
        });
      }
    });
  }

  // usamos esta variable como backup
  _allOptions: FormSelectMultipleItem[] = [];

  // Mostrar un spinner en lugar del icono (se recomienda deshabilitar el componente)
  _loading = false;
  @Input() set loading(val: boolean) {
    this._loading = coerceBooleanProperty(val);
  }

  // Flag menu abierto
  overlayIsOpen = false;

  // valor de la barra de busqueda
  searchValue = '';
  searchValueSubject = new BehaviorSubject<string>('');
  $searchValue = this.searchValueSubject.asObservable();

  _integratedSearch = true;
  // Definimos si realizamos una busqueda dentro del componente usando un criterio de comparacion de string con el Label
  // en caso de requerir una busqueda mas avanzada se debe realizar de forma externa
  @Input() set integratedSearch(val: boolean) {
    this._integratedSearch = coerceBooleanProperty(val);
  }

  _autoOrderSelected = true;
  // Ordenar automaticamente seleccionado primero (desactivar si hay un orden especifico Ej: productos)
  @Input() set autoOrderSelected(val: boolean) {
    this._autoOrderSelected = coerceBooleanProperty(val);
  }


  // Mostrar el boton X para eliminar
  _allowClearButton = false;
  @Input() set allowClearButton(val: boolean) {
    this._allowClearButton = coerceBooleanProperty(val);
  }

  _allowSearchMinimum = 5;
  // Minimo de opciones para permitir la barra de busqueda. Default 5, usar 0 para tener search siempre
  @Input() set allowSearchMinimum(val: number) {
    this._allowSearchMinimum = coerceNumberProperty(val);
  }

  @ViewChild('controlContainer')
  private controlContainerElement: ElementRef;

  // Evento de inicializacion en caso de usar NGIF
  @Output() initialized = new EventEmitter();
  // Subscripcion para la searchbar, setear la variable para que se desuscriba al eliminar el componente
  public searchSubscription = new Subscription();

  // tooltip en hover
  @Input() tooltip = '';
  // posiicon del tooltip
  @Input() tooltipPosition: 'left' | 'right' | 'above' | 'below' | 'before' | 'after' = 'above';
  // clase generica del tooltip (para añadir en styles.scss)
  @Input() tooltipClass = '';

  overlayPositionsBackup: ConnectionPositionPair[] = [
    {overlayY: 'top', overlayX: 'start', originX: 'end', originY: 'bottom'},
    {overlayY: 'bottom', overlayX: 'start', originX: 'end', originY: 'top'}
  ];
  overlayPositions: ConnectionPositionPair[] = [...this.overlayPositionsBackup];

  // Atributos de Form

  // Valor del Selector
  value: FormSelectMultipleItem[] = [];

  // Flag Touched
  touched = false;

  // Flag Disabled
  disabled = false;

  // Flag Control Valido
  validStatus = false;

  // Hacky, levantamos el control usando el mismo nombre de property que se usa para acceder al form
  @Input() formControlName: string;
  // Control del Form
  private control: AbstractControl;
  // Subscripcion al estado Valid
  private statusSubscription: Subscription = new Subscription();

  // Callback al cambiar el valor del input
  onChange = (val) => {};

  // Callback al cambiar a estado Touched
  onTouched = () => {};

  // En el constructor obtenemos el formGroup al cual pertenece el input
  destroySubject = new Subject<void>();
  constructor(
    @Optional() @Host() @SkipSelf()
    private controlContainer: ControlContainer,
    private detectionRef: ChangeDetectorRef,
  ) {
    // dado que al hacer un resize en la ventana no se ejecuta el detection changes, lo ejecutamos manualmente
    fromEvent(window, 'resize').takeUntil(this.destroySubject).subscribe(x => {
      this.forceDetectChanges();
    })
  }

  ngAfterViewInit() {
    this.initializeFormControl();
    window.setTimeout(() => {
      this.initialized.emit();
    });
  }

  ngOnDestroy() {
    this.statusSubscription.unsubscribe();
    this.searchSubscription.unsubscribe();
    this.destroySubject.next();
  }

  ngDoCheck() {
    if (this.control && this.control.touched && !this.touched) {
      this.markAsTouched();
    }
  }

  // Ejecutamos una busqueda interna
  executeIntegratedSearch() {
    // si encontramos un valor de busqueda, realizamos la busqueda pertinente
    if (this.searchValue.trim().length) {
      this._menuOptions = this._allOptions.filter(x => x.Label.toLowerCase().trim().includes(this.searchValue.toLowerCase().trim().toLowerCase()))
    } else {
      this._menuOptions = [...this._allOptions];
    }
  }

  forceDetectChanges() {
    // en caso de que el ciclo de detection changes no se ejecute, podemos llamar a esta funcion
    this.detectionRef.detectChanges();
  }

  initializeFormControl() {
    if (!this.controlContainer) {
      console.warn('Can\'t find parent FormGroup directive');
      return;
    }

    if (!this.formControlName) {
      console.warn('Missing FormControlName directive from host element of the component');
      return;
    }

    this.control = this.controlContainer.control.get(this.formControlName);
    this.validStatus = this.control.valid;
    this.statusSubscription = this.control.statusChanges.subscribe(x => {
      this.validStatus = !this.control.invalid; // usamos invalid pues valid posee un bug
    });
  }

  openMenu() {
    if (!this.disabled) {
      this.overlayIsOpen = true;
    }
  }

  closeMenu() {
    this.overlayIsOpen = false;
    this.searchValue = '';

    // al usar una busqueda externa, los valores deben mostrarse al cerrar son solo los seleccionados
    // construimos el array de opciones a partir de las opciones seleccionadas
    if (!this._integratedSearch) {
      this._menuOptions = [...this.value];
      this._allOptions = [...this.value];
    }

    this.markAsTouched();
    this.sortSelectedFirst();

  }

  // cuando hay un cambio en el select, llamamos a la callbackFunction onchange para que lo registre el form
  toggleItem(item: FormSelectMultipleItem, e: MouseEvent) {
    if (e.srcElement.id !== 'toggle' && e.srcElement.id !== 'toggleSpan') {
      return;
    }

    // en caso de que el valor por defecto NO sea un array, lo seteamos nosotros
    if (!Array.isArray(this.value)) {
      this.value = [];
    }

    if (!item.selected) {
      // intentar seleccionar
      if (!this.value || this.value.length === 0 || !this.value.find(x => x.Id === item.Id)) {
        item.selected = true;
        this.value.push(item);
        this.onChange(this.value);
      }
    } else {
      // intentar deseleccionar
      if (this.value && this.value.length && this.value.some(x => x.Id === item.Id)) {
        // usamos foreach para evitar las verificaciones de index, undefined, lugar desde donde se llama y otras yerbas
        this._menuOptions.forEach(x => {
          if (item.Id === x.Id) {
            // marcamos selected en false en caso de que se llame esta funcion desde las opciones
            x.selected = false;
          }
          // la quitamos del listado de seleccionados
          this.value = this.value.filter(y => y.Id !== item.Id);
          this.onChange(this.value);
        });
      }
    }
  }

  onSearchChanged() {
    // validamos si tenemos un search interno y seleccionamos solo uno para realizar el search solo una vez
    if (this._integratedSearch) {
      this.executeIntegratedSearch();
    } else {
      this.searchValueSubject.next(this.searchValue);
    }
    // disparamos el evento por si el sistema de overlay no acomoda el panel y necesita mas espacio
    window.setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 10);
  }

  // Ordenamos las opciones para que los seleccionados esten siempre arriba
  sortSelectedFirst() {
    if (!this._autoOrderSelected) {
      return;
    }
    this._menuOptions.sort((a, b) => {
      return (b.selected ? 1 : 0) - (a.selected ? 1 : 0);
    });
  }

  // Eliminar la seleccion
  clearValue() {
    if (this.disabled) {
      return;
    }
    this.value = [];
    this.onChange(this.value);
  }

  /** En caso de que se quiera modificar el valor por codigo
   * mediante AbstractControl.setValue(value) se llamará a este método
   * */
  writeValue(val: FormSelectMultipleItem[]) {
    if (!Array.isArray(val)) {
      console.warn(this.formControlName + ': El valor seteado no es un array. Recuerda que debes inicializarlo con: [[]]');
    } else {
      this.value = [...val];
      this._allOptions.forEach(option => {
        option.selected = this.value.some(val => val.Id === option.Id);
      });
      this._menuOptions.forEach(option => {
        option.selected = this.value.some(val => val.Id === option.Id);
      });
    }
  }

  /** El formulario registrara esta callbackFunction para notificar cuando hubo un
   * cambio en el formulario del lado del usuario
   * */
  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  /** El formulario registrara esta callbackFunction para notificar el estado Touched
   * */
  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  /** El formulario podra usar esta funcion para deshabilitar el control
   * */
  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  // Marcamos el control como Touched
  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

}

@Pipe({ name: 'multipleValuePipe', pure: false})
export class FormMultipleValuePipe implements PipeTransform {
  transform(value: FormSelectMultipleItem[], trimAmount: number = 0): string {
    if (!value || !value.length) {
      return '';
    }
    const label = trimAmount === 0 || value[0].Label.length <= trimAmount ? value[0].Label :
      (value[0].Label.substring(0, trimAmount) + '...');
    return label + (value.length > 1 ? ` y ${value.length - 1} más` : '');
  }
}

export interface FormSelectMultipleItem extends FormSelectItem {
  selected?: boolean;
}



// WEBPACK FOOTER //
// E:/jenkins_home/workspace/_agenda-unco-front-legacy_master/frontend-web/src/app/components/common/form-components/form-select-multiple/form-select-multiple.component.ts